import {Injectable} from '@angular/core';
import {EMPTY, from, Observable, of} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {Page} from '../utils/page';
import {ServiceConfigByCountryAndRole} from '../models/referential/service-config-by-country-and-role.model';
import {SynthesisViewConfig} from '../models/synthesis/synthesis-view-config.model';
import {EmployeeRoleEnum} from '../models/employee/employee-role.enum';
import {ServiceConfig} from '../models/referential/service-config.model';
import {map} from 'rxjs/operators';
import {Country} from '../models/referential/country.model';
import {ReferentialService} from './referential.service';
import {ParameterRoleService} from '../models/referential/parameter-role-service.model';
import {NumberOfChangeStock} from '../models/number-of-change-stock/number-of-change-stock';
import {SqliteServiceConfigRepository} from '../sqllite/repositories/params/sqlite-service-config.repository';
import {SqliteRoleParameterRepository} from '../sqllite/repositories/params/sqlite-role-parameter.repository';
import {SqliteCountryRepository} from '../sqllite/repositories/params/sqlite-country.repository';
import {SqliteAutomaticArticleRepository} from '../sqllite/repositories/params/sqlite-automatic-article.repository';
import {SqliteSynthesisViewConfigRepository} from '../sqllite/repositories/params/sqlite-synthesis-view-config.repository';
import {SqliteNumberOfChangeStockRepository} from '../sqllite/repositories/params/sqlite-number-of-change-stock.repository';
import {Option} from '../models/option/option.model';

@Injectable()
export class OfflineReferentialService extends ReferentialService {

    constructor(
        http: HttpClient,
        private readonly sqliteServiceConfigRepository: SqliteServiceConfigRepository,
        private readonly sqliteRoleParameterRepository: SqliteRoleParameterRepository,
        private readonly sqliteCountryRepository: SqliteCountryRepository,
        private readonly sqliteAutomaticArticleRepository: SqliteAutomaticArticleRepository,
        private readonly sqliteSynthesisViewConfigRepository: SqliteSynthesisViewConfigRepository,
        private readonly sqliteNumberOfChangeStockRepository: SqliteNumberOfChangeStockRepository) {
        super(http);
    }

    getAllServices(): Observable<Page<ServiceConfigByCountryAndRole>> {
        return from(this.sqliteServiceConfigRepository.getPagedServiceConfigsByCountryIsoCode());
    }

    getServiceConfigs(roleCode: EmployeeRoleEnum, countryIsoCode: string): Observable<ServiceConfig[]> {
        return from(this.sqliteServiceConfigRepository.getServiceConfigsByRoleCodeAndCountryIsoCode(roleCode.pricingRole, countryIsoCode));
    }

    getEnabledServiceConfigs(roleCode: EmployeeRoleEnum, countryIsoCode: string): Observable<ServiceConfig[]> {
        return this.getServiceConfigs(roleCode, countryIsoCode)
            .pipe(map(response => response.filter(item => item.isEnabled)));
    }

    getNumberOfChangeStock(): Observable<Page<NumberOfChangeStock>> {
        return from(this.sqliteNumberOfChangeStockRepository.getNumberOfChangeStock());
    }

    getReferential(country: string, role: EmployeeRoleEnum): Observable<any> {
        if (role == null || country == null) {
            return EMPTY;
        }
        return this.findRoleConfig(country, role);
    }


    getSynthesisViewConfig(): Observable<SynthesisViewConfig[]> {
        return from(this.sqliteSynthesisViewConfigRepository.getSynthesisViewConfigs()).pipe(
            map(response => this.getSortedSynthesisViewConfig(response))
        );
    }

    findRoleConfig(countryIsoCode: string, userRole: EmployeeRoleEnum): Observable<any> {
        return from(this.sqliteRoleParameterRepository.getRoleParameters())
            .pipe(
                map((data: ParameterRoleService[]) => {
                    let json: any = {};
                    for (const item of data) {
                        json[item.key] = item.value;
                    }
                    return json;
                })
            );
    }

    getAutomaticArticles(countryCode: string, roleCode: string, serviceCode: string): Observable<any> {
        return from(this.sqliteAutomaticArticleRepository.getAutomaticArticles(serviceCode));
    }

    getCountry(countryIsoCode: string): Observable<Country> {
        return from(this.sqliteCountryRepository.getCountryByIsoCode(countryIsoCode));
    }

    getOptions(countryIsoCode: string, roleCode: string): Observable<Page<Option>> {
        return of({
            content: [],
            totalElements: 0,
            totalPages: 0,
            size: 0,
            number: 0,
            numberOfElements: 0,
            first: true,
            last: true,
            empty: true
        });
    }

    getWashRateNLifetime(countryIsoCode: string, roleCode: string, serviceCode: string, subMarket: string, epi: string, family: string): Observable<any> {
        return of(null);
    }

    getGrossMarginRate(countryIsoCode: string, roleCode: string, serviceCode: string, subMarket: string, family: string, subFamily: string): Observable<any> {
        return of(null);
    }

}
